import { useViewerStore } from '../../../../../store/store';
import { AddIndividualMode, SwitchModuleIrrMat, SetModuleGraph } from './ModuleControl';
import type { Viewer } from '../Viewer-Factory/Viewer';
import type { ModuleMesh, SavedRoofModulesLayout } from '../../../../../@types/types';

/** Remove every module mesh from the viewer so a new AI layout starts from a clean roof. */
export function clearAllModuleMeshes(self: Viewer | null | undefined): void {
    if (!self?.roofMeshArr) return;

    self.roofMeshArr.forEach((roof: any) => {
        const moduleChildren = (roof.children ?? []).filter((c: any) => c.module);
        moduleChildren.forEach((m: ModuleMesh) => {
            const idx = self.meshArr.findIndex((mm: any) => mm.moduleId === m.moduleId);
            if (idx !== -1) self.meshArr.splice(idx, 1);
            const idx2 = self.moduleMeshArr.findIndex((mm: any) => mm.moduleId === m.moduleId);
            if (idx2 !== -1) self.moduleMeshArr.splice(idx2, 1);
            roof.remove(m);
        });
        roof.moduleArr = [];
    });

    useViewerStore.setState({ savedRoofModulesLayout: [] });
}

/** Clear module meshes on a subset of roofs before simulating a new layout variant. */
export function clearModulesOnRoofFaceIds(
    self: Viewer | null | undefined,
    roofFaceIds: string[],
): void {
    if (!self?.roofMeshArr || !roofFaceIds?.length) return;
    const idSet = new Set(roofFaceIds);
    self.roofMeshArr.forEach((roof: any) => {
        if (!idSet.has(roof.roofFaceId)) return;
        const moduleChildren = (roof.children ?? []).filter((c: any) => c.module);
        moduleChildren.forEach((m: ModuleMesh) => {
            const idx = self.meshArr.findIndex((mm: any) => mm.moduleId === m.moduleId);
            if (idx !== -1) self.meshArr.splice(idx, 1);
            const idx2 = self.moduleMeshArr.findIndex((mm: any) => mm.moduleId === m.moduleId);
            if (idx2 !== -1) self.moduleMeshArr.splice(idx2, 1);
            roof.remove(m);
        });
        roof.moduleArr = [];
    });
}

/**
 * Restore the exact initial oneDRAW default layout (module placement) from the store.
 * Falls back to the last saved layout when an explicit initial snapshot is not available.
 */
export async function restoreInitialDefaultLayout(self: Viewer | null | undefined): Promise<void> {
    if (!self) return;

    const state = useViewerStore.getState() as any;
    const initialLayout: SavedRoofModulesLayout = state.defaultLayoutModulesAtInitialLoad ?? [];
    const fallbackLayout: SavedRoofModulesLayout = state.savedRoofModulesLayout ?? [];
    const layout: SavedRoofModulesLayout =
        (initialLayout && initialLayout.length ? initialLayout : fallbackLayout) || [];

    if (!layout || layout.length === 0) {
        return;
    }

    const showLabel: boolean = !!state.isModuleMultiplierLabelVisible;

    clearAllModuleMeshes(self);

    // Re-add modules from saved layout
    layout.forEach((entry) => {
        const roofMesh = self.roofMeshArr.find((r: any) => r.roofFaceId === entry.roofFaceId);
        if (!roofMesh) return;
        entry.moduleArr.forEach((item) => {
            AddIndividualMode(roofMesh, item, true, self.moduleMeshArr, self.meshArr, showLabel);
        });
    });

    SwitchModuleIrrMat(self, true);
    if (typeof (self as any).refreshShade === 'function') {
        (self as any).refreshShade();
    }
    await SetModuleGraph(self as any);
}

/** Restore an exact module placement snapshot (e.g. AI category winner captured during simulation). */
export async function restoreModulesLayoutFromSnapshot(
    self: Viewer | null | undefined,
    layout: SavedRoofModulesLayout,
    options?: { skipShadeRefresh?: boolean },
): Promise<void> {
    if (!self || !Array.isArray(layout) || layout.length === 0) return;

    const showLabel: boolean = !!(useViewerStore.getState() as any).isModuleMultiplierLabelVisible;
    clearAllModuleMeshes(self);

    layout.forEach((entry) => {
        const roofMesh = self.roofMeshArr.find((r: any) => r.roofFaceId === entry.roofFaceId);
        if (!roofMesh) return;
        entry.moduleArr.forEach((item) => {
            AddIndividualMode(roofMesh, item, true, self.moduleMeshArr, self.meshArr, showLabel);
        });
    });

    useViewerStore.setState({ savedRoofModulesLayout: layout });
    // Callers in the AI apply flow run a single authoritative shade pass afterwards; let them skip
    // this internal refresh so shade isn't computed twice for one design selection. Re-colouring
    // has to skip the shade calc too, or SwitchModuleIrrMat starts the worker on its own.
    const skipShade = options?.skipShadeRefresh === true;
    SwitchModuleIrrMat(self, true, { skipShadeCalc: skipShade });
    if (!skipShade && typeof (self as any).refreshShade === 'function') {
        (self as any).refreshShade();
    }
    await SetModuleGraph(self as any);
}

